import React from "react";
import Image from "next/image";
import { Container, Col, Row, Tooltip, OverlayTrigger } from "react-bootstrap";
import Layout from "@/components/layout";
import dynamic from "next/dynamic";
import Link from "next/link";
import { useEffect, useState, useRef } from "react";
import moment from "moment-timezone";
import { healerServices } from "@/services/healerServices";
import { customerServices } from "@/services/customerServices";
import { useRouter } from "next/router";
import { useSelector, useDispatch } from "react-redux";
import { formatPhoneNumber } from "react-phone-number-input";
import { maskNumber } from "@/helper/common";
import { toast } from "react-toastify";
import { setPaymentData } from "@/redux/features/paymentSlice";
import {
  setModalityTypes,
  setSelectedModalityType,
  setClassForm,
} from "@/redux/features/healer/my-account/classSlice";
import styless from "@/styles/PageLoader.module.css";
import Head from "next/head";
import soft from "timezone-soft";

const OwlCarousel = dynamic(() => import("react-owl-carousel"), {
  ssr: false,
});

const CourseDetails = (props) => {
  const { modalityTypes, selectedFormData } = useSelector(
    (state) => state.healerClass
  );

  const [detailData, setDetailData] = useState(
    props.coursesDetailData.data ?? {}
  );
  const [courseDetailResponse, setCourseDetailResponse] = useState(
    props.coursesDetailData ?? {}
  );
  const [courseResponse, setCourseResponse] = useState(false);
  const { query } = useRouter();
  const query_id = query.detail[0].split("-");
  const query_length = query_id.length - 1;
  const class_query_id = query_id[0];
  const session_query_id = query_id[query_length];
  const dispatch = useDispatch();
  const router = useRouter();
  const [userType, setUserType] = useState(null);
  const { session, userData } = useSelector((state) => state.user);
  const [modalityType, setSelectedModalityType] = useState("");
  const [apiLoader, setApiLoader] = useState(true);
  const tzStrings = soft(
    moment.tz.guess(true) ? moment.tz.guess(true) : "America/Boise"
  );
  const [coursesDetailMeta, setCoursesDetailMeta] = useState(
    props?.coursesDetailMeta ?? {}
  );
  const [showSideButton, setShowSideButton] = useState(false);
  const firstSectionRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      if (!firstSectionRef.current) return;

      const sectionTop = firstSectionRef.current.getBoundingClientRect().bottom;

      setShowSideButton(sectionTop < 0);
    };

    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (userData?.roles?.length) {
      if (userData.roles[0].name == "Healer") {
        setUserType("Healer");
      } else {
        setUserType("Customer");
      }
    }
  }, [userData]);

  useEffect(() => {
    setDetailData(props.coursesDetailData.data);
  }, [props]);

  const proceedBooking = async () => {
    const appointmentBooking = JSON.parse(
      sessionStorage.getItem("appointmentBooking")
    );
    if (appointmentBooking && Object.keys(appointmentBooking).length) {
      sessionStorage.removeItem("appointmentBooking");
    }
    detailData.session_id = session_query_id;

    sessionStorage.setItem(
      "courseBooking",
      JSON.stringify({
        detailData,
      })
    );
    if (session && session.id) {
      const res = await customerServices.customerCheckClassAvailable({
        class_id: class_query_id,
        session_id: session_query_id,
        customer_id: session.id,
      });
      if (res.status) {
        if (detailData?.course?.is_free) {
          sessionStorage.setItem(
            "courseBooking",
            JSON.stringify({
              detailData: detailData,
              bookedSlotId: res.data.id,
            })
          );
          router.push({
            pathname: "/free-thank-you",
            query: {
              type: "course",
              id: res.data.id,
              healer_tz: detailData?.course?.healer?.time_zone,
            },
          });
        } else {
          router.push({
            pathname: "/payment",
            query: { type: "course", id: res.data.id },
          });
        }
      } else {
        toast.warning(res.message, {
          autoClose: 5000,
        });
        setCourseResponse(true);
      }
    } else {
      const res = await customerServices.customerCheckClassAvailable({
        class_id: class_query_id,
        session_id: session_query_id,
      });
      if (res.status) {
        detailData.session_id = session_query_id;
        sessionStorage.setItem(
          "courseBooking",
          JSON.stringify({
            detailData: detailData,
            bookedSlotId: res.data.id,
          })
        );
        router.push("/auth/customer/social/signin");
      } else {
        toast.warning(res.message, {
          autoClose: 5000,
        });
      }
    }
  };

  useEffect(() => {
    if (courseDetailResponse) {
      setApiLoader(false);
      sessionStorage.setItem("returnFlag", JSON.stringify(true));
      sessionStorage.removeItem("classRepayBookingId");
      dispatch(setPaymentData([]));
      if (courseDetailResponse.data?.course?.classes[0]?.status != 1) {
        router.push("/404");
      }
    }
  }, []);

  useEffect(() => {
    const fetchModalityTypes = async () => {
      const res = await healerServices.getModalities();
      if (res.status) {
        dispatch(
          setModalityTypes(
            res.data.map((i) => ({ value: i.id, label: i.name }))
          )
        );
      } else {
        toast.error(res.message, {
          autoClose: 5000,
        });
      }
    };
    fetchModalityTypes();
  }, [session.id]);

  useEffect(() => {
    if (detailData && modalityTypes) {
      const modality = detailData?.course?.classes[0]
        ? modalityTypes?.find(
            (i) => i.value == detailData?.course?.classes[0]?.modality_id
          )
        : "";
      setSelectedModalityType(modality);
    }
  }, [detailData, modalityTypes]);

  return (
    <>
      <Head>
        <title>{detailData?.course?.classes[0]?.meta_title}</title>
        <meta
          name="title"
          content={detailData?.course?.classes[0]?.meta_title}
        ></meta>
        <meta
          name="description"
          content={detailData?.course?.classes[0]?.meta_description}
        ></meta>
        <meta
          name="keywords"
          content={coursesDetailMeta?.meta?.meta_keywords}
        ></meta>
        <meta property="og:type" content="website"></meta>
        <meta
          property="og:title"
          content={detailData?.course?.classes[0]?.meta_title}
        ></meta>
        <meta
          property="og:image"
          content={coursesDetailMeta?.meta?.og_image}
        ></meta>
        <link
          rel="canonical"
          href={`https://www.zenkify.com/courses/${query.detail[0]}/detail`}
        ></link>
      </Head>

      {apiLoader ? (
        <div className={styless.overlay}>
          <div className={styless.loader}>
            <Image
              width={151}
              height={159}
              src="/images/logo.svg"
              alt="Zenkify Logo"
            />
          </div>
        </div>
      ) : (
        <>
          {detailData?.course?.healer ? (
            <>
              <section className="course-details-section cstum-corsedetails">
                <div className="outerbox-course">
                  <Container>
                    <Row>
                      <Col md={8} ref={firstSectionRef}>
                        <div className="content-course">
                          <h6 className="sub-title">
                            {modalityType?.label ?? "Not Specified"}
                          </h6>
                          <h1>
                            {detailData?.course?.classes[0]?.name ??
                              "Not Specified"}
                          </h1>
                          <p>
                            {detailData?.course?.classes[0]
                              ?.short_description ?? "Not Specified"}
                          </p>
                          <div className="profile-healer">
                            <div className="image">
                              <Image
                                width={90}
                                height={90}
                                src={
                                  !detailData?.course?.healer?.image
                                    ? "/images/dummy-user.svg"
                                    : detailData?.course?.healer?.image
                                }
                                alt="doctor"
                              />
                            </div>
                            <div className="content-profile">
                              <div className="doctor-name">
                                <Link
                                  href={`/healers/${detailData?.course?.healer?.slug}/detail`}
                                >
                                  <h4>
                                    {detailData?.course?.healer?.first_name +
                                      " " +
                                      detailData?.course?.healer?.last_name !=
                                    ""
                                      ? detailData?.course?.healer?.first_name +
                                        " " +
                                        detailData?.course?.healer?.last_name
                                      : "Not Specified"}
                                  </h4>
                                </Link>
                                <i className="ri-checkbox-circle-fill check-icon"></i>
                              </div>
                              <h6>
                                {detailData?.course?.healer?.detail
                                  ?.profile_tag ?? "Not Specified"}
                              </h6>
                              <div className="feedback">
                                <span>
                                  {[...Array(5).keys()].map((i, j) => (
                                    <>
                                      <i
                                        key={`j-${j}`}
                                        className={`ri-star-fill ${
                                          i + 1 <= detailData?.rating
                                            ? "star-fill"
                                            : ""
                                        }`}
                                      ></i>
                                    </>
                                  ))}
                                </span>
                                <p>
                                  {detailData?.course?.healer?.review?.length ??
                                    "Not Specified"}{" "}
                                  Feedback
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </Container>
                </div>
                <Container>
                  <Row>
                    <Col lg={8}>
                      <div className="content-course-body">
                        <div className="schedule-class">
                          <h4>
                            Schedule Class Date & Time{" "}
                            {
                              <OverlayTrigger
                                delay={{
                                  hide: 450,
                                  show: 300,
                                }}
                                overlay={(props) => (
                                  <Tooltip {...props}>
                                    {`(${tzStrings[0].standard.name})`}
                                  </Tooltip>
                                )}
                                placement="top"
                              >
                                <span
                                  style={{ cursor: "pointer" }}
                                >{`(${tzStrings[0].standard.abbr})`}</span>
                              </OverlayTrigger>
                            }
                          </h4>

                          <ul>
                            {detailData?.course?.classes?.map((item, index) => {
                              return (
                                <li key={index}>
                                  <span className="classname">
                                    {item?.name + " " + (index + 1) ??
                                      "Not Specified"}
                                  </span>
                                  <div className="date-time">
                                    <span>
                                      <i className="ri-calendar-line"></i>{" "}
                                      {item?.date_time
                                        ? moment
                                            .tz(
                                              item.date_time,
                                              detailData?.course?.healer
                                                ?.time_zone
                                            )
                                            .tz(moment.tz.guess(true))
                                            .format("D MMMM Y")
                                        : "Not Specified"}
                                    </span>
                                    <span>
                                      {" "}
                                      <i className="ri-time-line"></i>{" "}
                                      {item?.date_time
                                        ? moment
                                            .tz(
                                              item.date_time,
                                              detailData?.course?.healer
                                                ?.time_zone
                                            )
                                            .tz(moment.tz.guess(true))
                                            .format("hh:mm:ss A")
                                        : "Not Specified"}
                                    </span>
                                  </div>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                        <div className="course-overview">
                          <h4>Course Overview</h4>
                          {detailData?.course?.classes[0]?.description ? (
                            <div
                              dangerouslySetInnerHTML={{
                                __html:
                                  detailData?.course?.classes[0]?.description,
                              }}
                            />
                          ) : (
                            <p>Not Specified.</p>
                          )}
                        </div>
                      </div>
                    </Col>
                    <Col lg={4}>
                      <div className="enrollnow-card enroll-course">
                        <ul>
                          <li>
                            <span className="left-text">
                              <i className="ri-clipboard-line"></i>
                              <h6 className="totalfees">Total Fees</h6>
                            </span>
                            <span className="price">
                              {`$${detailData?.course?.total_price}` ??
                                "Not Specified"}
                            </span>
                          </li>
                          <li>
                            <span className="left-text">
                              <i className="ri-calendar-line"></i>
                              <h6>Duration</h6>
                            </span>
                            <span>
                              {detailData?.course?.classes[0]?.duration
                                ? detailData?.course?.classes[0]?.duration +
                                  " Days"
                                : "Not Specified"}
                            </span>
                          </li>
                          <li>
                            <span className="left-text">
                              <i className="ri-calendar-line"></i>
                              <h6>Session Type</h6>
                            </span>
                            <span>
                              {detailData?.course?.booking_type?.name
                                ? detailData?.course?.booking_type?.name ==
                                  "Both"
                                  ? "In-Person & Online"
                                  : detailData?.course?.booking_type?.name
                                : "Not Specified"}
                            </span>
                          </li>
                          <li>
                            <span className="left-text">
                              <i className="ri-file-text-line"></i>
                              <h6>Lessons</h6>
                            </span>
                            <span>
                              {detailData?.course?.classes.length ?? 0}
                            </span>
                          </li>
                          <li>
                            <span className="left-text">
                              <i className="ri-list-check"></i>
                              <h6>Available Slots</h6>
                            </span>
                            <span>
                              {`${
                                detailData?.course?.classes[0]
                                  ?.number_of_applicants - detailData?.bookings
                              }/${
                                detailData?.course?.classes[0]
                                  ?.number_of_applicants
                              }` ?? "Not Specified"}
                            </span>
                          </li>
                          <li>
                            <span className="left-text">
                              <i className="ri-calendar-line"></i>
                              <h6>Create Date</h6>
                            </span>
                            <span>
                              {moment
                                .tz(detailData?.course?.created_at, "UTC")
                                .tz(moment.tz.guess(true))
                                .format("MMMM Do YYYY") ?? "Not Specified"}
                              <OverlayTrigger
                                delay={{
                                  hide: 450,
                                  show: 300,
                                }}
                                overlay={(props) => (
                                  <Tooltip {...props}>
                                    {`(${tzStrings[0].standard.name})`}
                                  </Tooltip>
                                )}
                                placement="top"
                              >
                                <span
                                  style={{ cursor: "pointer" }}
                                >{` (${tzStrings[0].standard.abbr})`}</span>
                              </OverlayTrigger>
                            </span>
                          </li>
                        </ul>
                        <div className="CourseMobileFixItems">
                          <div className="register-fees">
                            <span>Registration Fees</span>
                            <span className="registar-price">
                              {`$${detailData?.course?.classes[0]?.registration_fee}` ??
                                "N/A"}
                            </span>
                          </div>
                          <button
                            className="theme-btn"
                            disabled={courseResponse || userType == "Healer"}
                            onClick={() => proceedBooking()}
                          >
                            Proceed to Pay
                          </button>
                        </div>
                        {/* <div className="card-footer">
                          {detailData?.course?.healer?.country_code != null &&
                          detailData?.course?.healer?.mobile_number != null ? (
                            <>
                              <h6>For details about the course</h6>
                              <Link
                                href={`tel:${
                                  session?.id
                                    ? detailData?.course?.healer?.country_code +
                                      detailData?.course?.healer?.mobile_number
                                    : ""
                                }`}
                                className="callus-btn"
                              >
                                <i className="ri-phone-line"></i> Healer&apos;s
                                Contact No.:{" "}
                                <span>
                                  {session?.id ? (
                                    detailData?.course?.healer?.country_code +
                                    " " +
                                    formatPhoneNumber(
                                      detailData?.course?.healer?.country_code +
                                        detailData?.course?.healer
                                          ?.mobile_number
                                    )
                                  ) : (
                                    <Link
                                      href="/auth/customer/social/signin"
                                      title="Sign In To View Mobile Number"
                                    >
                                      {maskNumber(
                                        detailData?.course?.healer
                                          ?.mobile_number ?? "0000000000"
                                      )}
                                    </Link>
                                  )}
                                </span>
                              </Link>
                            </>
                          ) : (
                            <>
                              <h6>For details about the course</h6>
                              <i className="ri-phone-line"></i> Call Us:
                              <span> Not Specified</span>
                            </>
                          )}
                        </div> */}
                      </div>
                    </Col>
                  </Row>
                </Container>
              </section>
              {detailData?.otherCourses?.length != 0 ? (
                <section className="ourcourses-section">
                  <Container>
                    <div className="row courses-head-img">
                      <Col md={2}>
                        <Image
                          width={130}
                          height={76}
                          src="/images/hand-left.svg"
                          alt=""
                        />
                      </Col>
                      <Col md={8}>
                        <div className="section-title">
                          <h3 className="title">courses offered</h3>
                          <p>
                            Our verified healers are providing various courses
                            in all different modalities which anyone can learn
                            and get certified to help connect with your inner
                            self and practice self care.
                          </p>
                        </div>
                      </Col>
                      <Col md={2}>
                        <Image
                          width={130}
                          height={81}
                          src="/images/hand-right.svg"
                          alt=""
                        />
                      </Col>
                    </div>
                    <Col md={12}>
                      {detailData?.otherCourses?.length != 0 ? (
                        <OwlCarousel
                          className="courses-slider"
                          loop={false}
                          margin={15}
                          nav={false}
                          dots={true}
                          responsive={{
                            0: {
                              items: 1,
                            },
                            600: {
                              items: 3,
                            },
                            1000: {
                              items: 4,
                            },
                          }}
                        >
                          {detailData?.otherCourses?.map((item, index) => {
                            return (
                              <Col className="mb-3" key={index}>
                                <div className="courses-card">
                                  <div className="courses-content">
                                    <h3>
                                      {item?.class?.name ?? "Not Specified"}
                                    </h3>
                                    <h4>
                                      {item?.class?.short_description ??
                                        "Not Specified"}
                                    </h4>
                                    <ul>
                                      <li>
                                        <i className="ri-time-line"></i>{" "}
                                        <span>
                                          {item?.class?.duration
                                            ? item?.class?.duration + " Days"
                                            : "Not Specified"}
                                        </span>
                                      </li>
                                      <li>
                                        <i className="ri-user-3-line"></i>{" "}
                                        <span>
                                          {item?.booking_type?.name
                                            ? item?.booking_type?.name == "Both"
                                              ? "In-Person & Online"
                                              : item?.booking_type?.name
                                            : "Not Specified"}
                                        </span>
                                      </li>
                                    </ul>
                                    <Link
                                      href={`/courses/${item.slug}/detail`}
                                      onClick={() => setDetailData({})}
                                      className="theme-btn"
                                    >
                                      Book Class{" "}
                                      <i className="ri-arrow-right-line"></i>
                                    </Link>
                                  </div>
                                </div>
                              </Col>
                            );
                          })}
                        </OwlCarousel>
                      ) : (
                        <p>Not Available</p>
                      )}
                    </Col>
                    <Col md={12} className="mt-3 text-center">
                      <Link href={"/courses"} className="theme-btn">
                        Explore More Courses{" "}
                        <i className="ri-arrow-right-line"></i>
                      </Link>
                    </Col>
                  </Container>
                </section>
              ) : (
                ""
              )}
            </>
          ) : (
            ""
          )}
        </>
      )}
      <Link
        id="floatingBook"
        className="floating-book"
        aria-label="Book a FREE call now"
        href="/healers"
        style={{ opacity: showSideButton ? 1 : 0 }}
      >
        Book a FREE call now
      </Link>
    </>
  );
};

CourseDetails.getLayout = function getLayout(page) {
  return <Layout>{page}</Layout>;
};

export default CourseDetails;

// ssr
export async function getServerSideProps(context) {
  const { query } = context;
  const fetchCoursesDetailData = async () => {
    try {
      const res = await healerServices.getHealerCourseDetails({
        slug: query.detail[0],
        timezone: moment.tz.guess(true),
      });
      if (res.status) {
        return res;
      }
    } catch (error) {
      console.error("Error fetching slider data:", error);
    }
  };

  const fetchCoursesDetailMeta = async () => {
    try {
      const res = await healerServices.getMetaInformation("course-detail");
      if (res.status) {
        return res.data;
      }
    } catch (error) {
      console.error("Error fetching slider data:", error);
    }
  };

  const coursesDetailMeta = (await fetchCoursesDetailMeta()) ?? {};
  const coursesDetailData = (await fetchCoursesDetailData()) ?? {};

  // Pass data to the page via props
  return { props: { coursesDetailMeta, coursesDetailData } };
}
